import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { backgroundValidationSchema } from "../../validations/validations";
import { INITIAL_BACKGROUND_SETTINGS } from "../configs";
import { DEFAULT_BACKGROUND_URL } from "../constants";
import { IApiBackground, IApiBackgroundImage, IBackgroundFormData, IBackgroundImageFormData } from "../types";
import { mergeKioskSettings } from "../utils/mergeKioskSettings";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";
import useUploadAssets from "./useUploadAssets";

interface UseBackgroundSettingsProps {
    characterId: string;
}

const createDefaultBackgroundImage = (initialData?: { url: string }): IBackgroundImageFormData => ({
    file: null,
    url: initialData?.url || "",
});

const DEFAULT_BACKGROUND_FORM: IBackgroundFormData = {
    portrait: createDefaultBackgroundImage(INITIAL_BACKGROUND_SETTINGS.v),
    landscape: createDefaultBackgroundImage(INITIAL_BACKGROUND_SETTINGS.h),
};

export const useBackgroundSettings = ({ characterId }: UseBackgroundSettingsProps) => {
    const { t } = useTranslation();
    const { kioskSetting, isLoadingKioskSetting, isUpdatingKioskSetting, updateSettings } = useKioskSettings({
        characterId,
    });
    const { handleUploadFile, isUploading } = useUploadAssets();

    // Track preview URLs for uploaded files
    const [previewUrls, setPreviewUrls] = useState<{
        portrait: string | null;
        landscape: string | null;
    }>({
        portrait: null,
        landscape: null,
    });

    // ======================= Form Setup =======================
    const methods = useForm<IBackgroundFormData>({
        defaultValues: DEFAULT_BACKGROUND_FORM,
        resolver: yupResolver(backgroundValidationSchema()),
        mode: "onChange",
    });

    const { control, handleSubmit, formState, reset, setValue } = methods;

    const watchedPortrait = useWatch({ control, name: "portrait" });
    const watchedLandscape = useWatch({ control, name: "landscape" });

    // ======================= Preview URL Management =======================
    // Update preview URL when file changes
    const updatePreviewUrl = useCallback((type: "portrait" | "landscape", file: File | null) => {
        setPreviewUrls((prev) => {
            // Revoke old URL to prevent memory leaks
            const oldUrl = prev[type];
            if (oldUrl) {
                URL.revokeObjectURL(oldUrl);
            }

            return {
                ...prev,
                [type]: file ? URL.createObjectURL(file) : null,
            };
        });
    }, []);

    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            Object.values(previewUrls).forEach((url) => {
                if (url) URL.revokeObjectURL(url);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Get display URL for an image (preview URL, uploaded URL, or default)
    const getDisplayUrl = useCallback(
        (type: "portrait" | "landscape"): string => {
            const previewUrl = previewUrls[type];
            if (previewUrl) {
                return previewUrl;
            }

            const imageData = type === "portrait" ? watchedPortrait : watchedLandscape;

            if (imageData?.url && imageData.url.trim() !== "") {
                return imageData.url;
            }

            return DEFAULT_BACKGROUND_URL;
        },
        [previewUrls, watchedPortrait, watchedLandscape]
    );

    // ======================= Data Transformation =======================
    const transformAPItoForm = useCallback((apiData: IApiBackground | undefined): IBackgroundFormData => {
        if (!apiData) {
            return DEFAULT_BACKGROUND_FORM;
        }

        // Transform API background image to form data
        const transformBackgroundImage = (img: IApiBackgroundImage | undefined): IBackgroundImageFormData => ({
            file: null,
            url: img?.url || "",
        });

        return {
            portrait: transformBackgroundImage(apiData.v),
            landscape: transformBackgroundImage(apiData.h),
        };
    }, []);

    const transformFormToAPI = useCallback((formData: IBackgroundFormData): IApiBackground => {
        const now = new Date().toISOString();

        // Always send position_x and position_y as 0
        const createApiBackgroundImage = (img: IBackgroundImageFormData): IApiBackgroundImage => ({
            url: img.url, // Send empty string if no image uploaded
            updated_time: now,
            position_x: 0,
            position_y: 0,
        });

        // Always send both v and h objects
        return {
            v: createApiBackgroundImage(formData.portrait),
            h: createApiBackgroundImage(formData.landscape),
        };
    }, []);

    // ======================= File Handlers =======================
    const handleFileSelect = useCallback(
        (type: "portrait" | "landscape", file: File | null) => {
            const fieldName = type;

            if (file) {
                setValue(`${fieldName}.file`, file, { shouldDirty: true });
                updatePreviewUrl(type, file);
            } else {
                setValue(`${fieldName}.file`, null, { shouldDirty: true });
                setValue(`${fieldName}.url`, "", { shouldDirty: true });
                updatePreviewUrl(type, null);
            }
        },
        [setValue, updatePreviewUrl]
    );

    // ======================= Upload Handler =======================
    const uploadBackgroundImage = useCallback(
        async (imageData: IBackgroundImageFormData): Promise<IBackgroundImageFormData> => {
            if (!imageData.file) {
                return imageData;
            }

            const response = await handleUploadFile({
                file: imageData.file,
                prefix: "image",
            });

            if (response?.url) {
                return {
                    ...imageData,
                    url: response.url,
                    file: null,
                };
            }

            throw new Error(t("COMMON.UPLOAD_FAILED") || "Upload failed");
        },
        [handleUploadFile, t]
    );

    // ======================= Submit Handler =======================
    const onSubmit = useCallback(
        async (data: IBackgroundFormData) => {
            try {
                // Upload pending files
                if (data.portrait.file) {
                    data.portrait = await uploadBackgroundImage(data.portrait);
                    setValue("portrait", data.portrait);
                    updatePreviewUrl("portrait", null);
                }
                if (data.landscape.file) {
                    data.landscape = await uploadBackgroundImage(data.landscape);
                    setValue("landscape", data.landscape);
                    updatePreviewUrl("landscape", null);
                }

                const apiPayload = transformFormToAPI(data);
                const currentNative = kioskSetting?.data?.native;
                const mergedNative = mergeKioskSettings(currentNative, { background: apiPayload });

                await updateSettings({ native: mergedNative });

                // Reset form with new values to clear dirty state
                reset(data);
                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
            } catch (error) {
                customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
            }
        },
        [uploadBackgroundImage, transformFormToAPI, kioskSetting, updateSettings, setValue, reset, t, updatePreviewUrl]
    );

    const saveHandler = useCallback(async () => {
        await handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit]);

    useTabGuard("background", formState.isDirty, saveHandler);

    // ======================= Initialize Form from API =======================
    useEffect(() => {
        if (!isLoadingKioskSetting) {
            const backgroundData = kioskSetting?.data?.native?.background;
            const formData = transformAPItoForm(backgroundData);
            reset(formData);
        }
    }, [kioskSetting?.data?.native?.background, isLoadingKioskSetting, reset, transformAPItoForm]);

    // ======================= Computed Values =======================
    const hasCustomImage = useCallback(
        (type: "portrait" | "landscape"): boolean => {
            const imageData = type === "portrait" ? watchedPortrait : watchedLandscape;

            return Boolean(imageData?.file) || Boolean(imageData?.url && imageData.url.trim() !== "");
        },
        [watchedPortrait, watchedLandscape]
    );

    return {
        // Form methods
        methods,
        handleSubmit,
        onSubmit,
        formState,

        // State
        watchedPortrait,
        watchedLandscape,

        // Loading states
        isLoadingKioskSetting,
        isUpdatingKioskSetting,
        isUploading,

        // Handlers
        handleFileSelect,

        // Helpers
        getDisplayUrl,
        hasCustomImage,

        // Constants
        DEFAULT_BACKGROUND_URL,
    };
};

export default useBackgroundSettings;
